import React, { useEffect, useState, useRef } from "react";

interface LogEntry {
  time: string;
  message: string;
  type: "info" | "success" | "warn";
}

const INITIAL_LOGS: LogEntry[] = [
  { time: "08:42:15", message: "System initialized", type: "info" },
  { time: "08:42:16", message: "Loading neural weights...", type: "info" },
  { time: "08:42:18", message: "TensorFlow backend ready", type: "success" },
  { time: "08:42:19", message: "API gateway: 200 OK", type: "success" },
  { time: "08:42:20", message: "Database pool: 12 connections", type: "info" },
  { time: "08:42:21", message: "Cache hit ratio: 94.2%", type: "success" },
  { time: "08:42:22", message: "WebSocket: connected", type: "success" },
  { time: "08:42:23", message: "Monitoring active", type: "info" },
];

const EXTRA_LOGS: LogEntry[] = [
  { time: "09:15:33", message: "Deploying microservice v2.4.1", type: "info" },
  { time: "09:15:35", message: "Health check: PASS", type: "success" },
  { time: "09:15:36", message: "Latency: 12ms p99", type: "success" },
  { time: "09:15:38", message: "Auto-scaling triggered", type: "warn" },
  { time: "09:15:40", message: "New instance: i-0a3f2c8d", type: "info" },
  { time: "09:15:42", message: "Load balancer: rebalancing", type: "info" },
  { time: "09:15:45", message: "All systems nominal", type: "success" },
];

export const SystemStatus: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>(INITIAL_LOGS);
  const [time, setTime] = useState(new Date());
  const [cpuLoad, setCpuLoad] = useState([30, 45, 35, 50, 40, 55, 48, 42]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const logIndex = useRef(0);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCpuLoad((prev) => {
        const next = [...prev.slice(1), Math.floor(Math.random() * 30) + 30];
        return next;
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (logIndex.current < EXTRA_LOGS.length) {
        const entry = EXTRA_LOGS[logIndex.current];
        const now = new Date();
        const timeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
        setLogs((prev) => [...prev.slice(-12), { ...entry, time: timeStr }]);
        logIndex.current++;
      } else {
        logIndex.current = 0;
      }
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  const formatTime = (d: Date) =>
    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;

  return (
    <div className="w-full max-w-[420px] mx-auto lg:max-w-none border border-border bg-bg-elevated font-mono text-xs">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-bg-surface/50">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
          <span className="text-text-secondary tracking-wider uppercase text-[10px]">
            System Status
          </span>
        </div>
        <span className="text-text-muted">{formatTime(time)} UTC</span>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 border-b border-border">
        <div className="px-4 py-3 border-r border-border">
          <div className="text-text-muted text-[10px] uppercase tracking-wider mb-1">
            Uptime
          </div>
          <div className="text-text-primary text-sm">99.97%</div>
        </div>
        <div className="px-4 py-3 border-r border-border">
          <div className="text-text-muted text-[10px] uppercase tracking-wider mb-1">
            Latency
          </div>
          <div className="text-accent text-sm">12ms</div>
        </div>
        <div className="px-4 py-3">
          <div className="text-text-muted text-[10px] uppercase tracking-wider mb-1">
            Requests
          </div>
          <div className="text-text-primary text-sm">2.4M</div>
        </div>
      </div>

      {/* CPU Graph */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-text-muted text-[10px] uppercase tracking-wider">
            CPU Load
          </span>
          <span className="text-accent text-[10px]">
            {cpuLoad[cpuLoad.length - 1]}%
          </span>
        </div>
        <div className="flex items-end gap-0.5 h-8">
          {cpuLoad.map((val, i) => (
            <div
              key={i}
              className="flex-1 bg-accent/20 rounded-sm transition-all duration-500"
              style={{ height: `${val}%` }}
            >
              <div
                className="w-full bg-accent/60 rounded-sm transition-all duration-500"
                style={{ height: `${Math.min(val * 1.2, 100)}%` }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Log stream */}
      <div
        ref={scrollRef}
        className="px-4 py-3 h-[140px] overflow-y-auto scrollbar-hide"
      >
        {logs.map((log, i) => (
          <div key={i} className="flex gap-3 py-0.5">
            <span className="text-text-muted shrink-0">{log.time}</span>
            <span
              className={`${
                log.type === "success"
                  ? "text-accent"
                  : log.type === "warn"
                    ? "text-warning"
                    : "text-text-secondary"
              }`}
            >
              {log.message}
            </span>
          </div>
        ))}
        <div className="flex gap-3 py-0.5">
          <span className="text-text-muted shrink-0">{formatTime(time)}</span>
          <span className="text-text-muted animate-pulse">_</span>
        </div>
      </div>
    </div>
  );
};
